import { FormGroup, FormLabel, MenuItem, Select, Stack, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useEffect, useState } from "react";
import { DailyScheduleBox } from "./DailyScheduleBox";
import { MonthlyScheduleBox } from "./MonthlyScheduleBox";
import { YearlyScheduleBox } from "./YearlyScheduleBox";
import { TemplateData } from "../../../types";
import dayjs, { Dayjs } from "dayjs";

interface ScheduleBoxProps {
  template: TemplateData;
  setTemplate: (val: TemplateData) => void;
}

export const ScheduleBox = ({ template, setTemplate }: ScheduleBoxProps) => {
  const [startDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [startTime, setSelectedTime] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [endTime, setEndTime] = useState<Dayjs | null>(null);

  useEffect(() => {
    const initialStartDateTimeString = template.schedule?.start_at;
    if (initialStartDateTimeString) {
      const initialDateTime = dayjs(initialStartDateTimeString, 'YYYY_MM_DD HH:mm:ss');
      setSelectedDate(initialDateTime);
      setSelectedTime(initialDateTime);
    }

    const initialEndDateTimeString = template.schedule?.end_at;
    if (initialEndDateTimeString) {
      const initialDateTime = dayjs(initialEndDateTimeString, 'YYYY_MM_DD HH:mm:ss');
      setEndDate(initialDateTime);
      setEndTime(initialDateTime);
    }
  }, [template]);

  const updateStartAt = (date: Dayjs | null, time: Dayjs | null)  => {
    if (date) {
      const start_at = dayjs(date)
        .set('hour', time?.hour() ?? 0)
        .set('minute', time?.minute() ?? 0)
        .set('second', time?.second() ?? 0)
        .format('YYYY-MM-DD HH:mm:ss');
      setTemplate({ ...template, schedule: { ...template.schedule ?? {}, start_at } });
    }
  }

  const updateEndAt = (date: Dayjs | null, time: Dayjs | null)  => {
    if (date) {
      const end_at = dayjs(date)
      .set('hour', time?.hour() ?? 23)
      .set('minute', time?.minute() ?? 59)
      .set('second', time?.second() ?? 59)
        .format('YYYY-MM-DD HH:mm:ss');
      setTemplate({ ...template, schedule: { ...template.schedule ?? {}, end_at } });
    }
  }

  const handlestartDateChange = (newDate: Dayjs | null) => {
    setSelectedDate(newDate);
    updateStartAt(newDate, startTime);
  };

  const handleStartTimeChange = (newTime: Dayjs | null) => {
    setSelectedTime(newTime);
    updateStartAt(startDate, newTime);
  };

  const handleEndDateChange = (newDate: Dayjs | null) => {
    setEndDate(newDate);
    updateEndAt(newDate, endTime);

  };

  const handleEndTimeChange = (newTime: Dayjs | null) => {
    setEndTime(newTime);
    updateEndAt(endDate, newTime);
  };

  return (
    <Stack spacing={2}>
      <FormGroup>
        <FormLabel>Schedule/Target Name</FormLabel>
        <TextField
          variant="outlined"
          sx={{ width: '100%' }}
          value={template.schedule?.name || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setTemplate({ ...template, schedule: { ...template.schedule ?? {}, name: e.target.value } })
          }
        />
      </FormGroup>
      <FormGroup>
        <FormLabel>Starts</FormLabel>
        <Stack direction="row" justifyContent="space-between">
          <Stack width="60%">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={startDate}
                onChange={handlestartDateChange}
                format="DD/MM/YYYY"
              />
            </LocalizationProvider>
          </Stack>
          <Stack width="35%">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                value={startTime}
                onChange={handleStartTimeChange}
                ampm={false}
              />
            </LocalizationProvider>
          </Stack>
        </Stack>
      </FormGroup>
      <FormGroup>
        <FormLabel>Ends</FormLabel>
        <Stack direction="row" justifyContent="space-between">
          <Stack width="60%">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker format="DD/MM/YYYY" value={endDate} onChange={handleEndDateChange} />
            </LocalizationProvider>
          </Stack>
          <Stack width="35%">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker value={endTime} onChange={handleEndTimeChange} ampm={false} />
            </LocalizationProvider>
          </Stack>
        </Stack>
      </FormGroup>
      <FormGroup>
        <FormLabel>Repeats</FormLabel>
        <Select
          value={template.schedule?.repeats || "never"}
          // @ts-ignore
          onChange={(e) => setTemplate({ ...template, schedule: { ...template.schedule ?? {}, repeats: e.target.value, repeat_period: {} } })}
        >
          <MenuItem value="never">Never</MenuItem>
          <MenuItem value="daily">Daily</MenuItem>
          <MenuItem value="monthly">Monthly</MenuItem>
          <MenuItem value="yearly">Yearly</MenuItem>
        </Select>
      </FormGroup>
      <FormGroup>
        {template.schedule?.repeats === "never" && <></>}
        {template.schedule?.repeats === "daily" && <DailyScheduleBox template={template} setTemplate={setTemplate} />}
        {template.schedule?.repeats === "monthly" && <MonthlyScheduleBox template={template} setTemplate={setTemplate} />}
        {template.schedule?.repeats === "yearly" && <YearlyScheduleBox template={template} setTemplate={setTemplate} />}
      </FormGroup>
    </Stack>
  );
};
